import Text from 'components/atoms/Text';
import DashboardCard from '../DashboardCard';
import DashboardStatusCard from '../DashboardStatusCard';
import StateTotalDonutChart from '../StateTotalDonutChart';
import Top5MonthlyIncidentsGraph from '../Top5MonthlyIncidentsGraph';
import DashboardAlloc8SyncWidget from '../DashboardAlloc8SyncWidget';
import IncidentMappingFailuresByDateGraph from '../IncidentMappingFailuresByDateGraph';

export interface IDashboardIncidentsOverviewLayout {
	data: any;
	isAdminManager: boolean;
	updatedTime: string;
	setUpdatedTime: (time: string) => void;
	dateFrom?: string;
}

const DashboardIncidentsOverviewLayout: React.FC<
	IDashboardIncidentsOverviewLayout
> = ({ data, isAdminManager, updatedTime, setUpdatedTime, dateFrom }) => {
	const incidentsInCurrentYearCard = (
		<DashboardCard
			className="mb-[30px]"
			title="Incidents in Current Financial Year"
		>
			<Text className="text-[22px] font-semibold mb-2">
				<span className="text-gray-900">
					{data.currentYear.incident.totalOpen}
				</span>{' '}
				Open Incident Reports
			</Text>
			<progress
				id="totalInYear"
				value={data.currentYear.incident.totalOpen}
				max={data.currentYear.incident.total}
				className="w-full percentbar"
			/>
			<Text className="text-[14px]">
				Total of {data.currentYear.incident.total} incident reports.
			</Text>
		</DashboardCard>
	);

	const alloc8SyncCard = (
		<DashboardCard
			className="mb-[30px]"
			title="Incidents Status - Today"
			headerExtra={
				<Text className="text-[12px] text-gray-500">
					Updated: {updatedTime}
				</Text>
			}
		>
			<DashboardAlloc8SyncWidget
				getUpdatedTime={() => {
					const now = new Date();
					const timeString = now.toLocaleTimeString('en-AU', {
						hour: '2-digit',
						minute: '2-digit',
						hour12: false,
					});
					setUpdatedTime(timeString);
					return timeString;
				}}
			/>
		</DashboardCard>
	);

	const stateTotalsCard = (
		<DashboardCard
			className="w-full items-stretch h-full"
			title="State Totals for Current Financial Year"
			contentMaxHeight="flex-col justify-center items-center"
		>
			<StateTotalDonutChart states={data.stateTotals} />
		</DashboardCard>
	);

	const top5BranchesCard = (
		<DashboardCard
			className="w-full items-stretch h-full"
			title="Top 5 Branches - Monthly"
		>
			<Top5MonthlyIncidentsGraph monthData={data.currentMonthData} />
		</DashboardCard>
	);

	const incidentMappingFailuresCard = (
		<DashboardCard
			className="w-full items-stretch h-full"
			title="Incident Mapping Failures by Date"
		>
			<IncidentMappingFailuresByDateGraph />
		</DashboardCard>
	);

	const incidentsByBranchCard = (
		maxHeight: string,
		className: string = 'w-full overflow-y-auto'
	) => (
		<DashboardCard
			className={className}
			total={data.incidentStatusData.total}
			dividerColor="border-scorpion-200"
			title="Incidents by Branch"
			contentMaxHeight={maxHeight}
		>
			<DashboardStatusCard
				incidentPath="incidents"
				branches={data.incidentStatusData.branches}
				dateFrom={dateFrom}
				isAdminManager={isAdminManager}
			/>
		</DashboardCard>
	);

	if (isAdminManager) {
		return (
			<>
				<div className="flex mt-8 space-x-[30px] items-stretch max-h-[700px]">
					<div className="flex flex-col basis-2/5 max-w-[620px] min-w-[472px] items-stretch">
						{incidentsInCurrentYearCard}
						{alloc8SyncCard}
					</div>
					<div className="flex flex-col basis-3/5 items-stretch grow">
						<div className="flex space-x-[30px] h-2/3 mb-[30px]">
							<div className="basis-1/3">{stateTotalsCard}</div>
							<div className="basis-2/3">{top5BranchesCard}</div>
						</div>
						<div className="h-1/3">{incidentMappingFailuresCard}</div>
					</div>
				</div>
				<div className="flex mt-[30px] space-x-[30px]">
					{incidentsByBranchCard('max-h-[430px]')}
				</div>
			</>
		);
	}

	return (
		<div className="flex mt-8 space-x-[30px] items-stretch max-h-[700px]">
			<div className="flex flex-col basis-2/5 max-w-[620px] min-w-[472px] items-stretch">
				{incidentsInCurrentYearCard}
				{incidentsByBranchCard(
					'max-h-[450px]',
					'basis-1/3 overflow-y-auto grow'
				)}
			</div>
			<div className="flex flex-col basis-3/5 items-stretch grow">
				<div className="flex space-x-[30px] h-2/3 mb-[30px]">
					<div className="basis-1/3">{stateTotalsCard}</div>
					<div className="basis-2/3">{top5BranchesCard}</div>
				</div>
				<div className="h-1/3">{incidentMappingFailuresCard}</div>
			</div>
		</div>
	);
};

export default DashboardIncidentsOverviewLayout;
